/*
 * Carbisdale - RichTextOps
 *
 * Pure Functions für Rich-Text-Operationen.
 * Arbeitet mit FormatSpan[] und LinkEntry[] statt Markdown-Markern.
 *
 * FormatSpan: { start: number, end: number, type: string }
 *   - start inklusiv, end exklusiv
 *   - type: 'bold' | 'italic' | 'underline' | 'strikethrough'
 *
 * LinkEntry: { offset: number, targetId: number }
 *   - offset = Position des \uFFFC im Text
 *
 * Alle Funktionen erzeugen neue Arrays (Immutabilität).
 * Ergebnis-Spans sind immer nach start sortiert.
 */

import { findFormatSpans, findLinkSpans, findUrlSpans } from './TextProcessor.js'

// ===================================================================
// Kategorie A: Span-Adjustment (bei Text-Mutationen)
// ===================================================================

/**
 * Passt Spans nach einer Einfügung an.
 * - Spans nach offset werden verschoben
 * - Spans die offset enthalten wachsen
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} offset - Einfüge-Position
 * @param {number} length - Anzahl eingefügter Zeichen
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function adjustSpansForInsert(spans, offset, length) {
    if (length <= 0) return spans.map(s => ({ ...s }))
    return spans.map(s => {
        if (s.end <= offset) {
            // Span komplett vor Insert → unverändert
            return { ...s }
        }
        if (s.start > offset) {
            // Span komplett nach Insert → verschieben
            return { ...s, start: s.start + length, end: s.end + length }
        }
        // Span enthält offset (start <= offset < end) → Span wächst
        return { ...s, end: s.end + length }
    })
}

/**
 * Passt Spans nach dem Löschen eines einzelnen Zeichens an.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} offset - Position des gelöschten Zeichens
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function adjustSpansForDelete(spans, offset) {
    const result = []
    for (const s of spans) {
        if (s.end <= offset) {
            // Span komplett vor Löschung → unverändert
            result.push({ ...s })
        } else if (s.start > offset) {
            // Span komplett nach Löschung → verschieben
            result.push({ ...s, start: s.start - 1, end: s.end - 1 })
        } else {
            // Span enthält offset → schrumpfen
            const newEnd = s.end - 1
            if (newEnd > s.start) {
                result.push({ ...s, end: newEnd })
            }
            // start >= end → Span entfernen
        }
    }
    return result
}

/**
 * Passt Spans nach dem Löschen eines Bereichs an.
 * 6 Fälle: vor, nach, innerhalb, links-überlappend, rechts-überlappend, umschließend
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} rangeStart - Anfang des gelöschten Bereichs (inklusiv)
 * @param {number} rangeEnd - Ende des gelöschten Bereichs (exklusiv)
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function adjustSpansForDeleteRange(spans, rangeStart, rangeEnd) {
    if (rangeStart >= rangeEnd) return spans.map(s => ({ ...s }))
    const deleteLen = rangeEnd - rangeStart
    const result = []
    for (const s of spans) {
        if (s.end <= rangeStart) {
            // Fall 1: Span komplett vor dem gelöschten Bereich
            result.push({ ...s })
        } else if (s.start >= rangeEnd) {
            // Fall 2: Span komplett nach dem gelöschten Bereich
            result.push({ ...s, start: s.start - deleteLen, end: s.end - deleteLen })
        } else if (s.start >= rangeStart && s.end <= rangeEnd) {
            // Fall 3: Span komplett innerhalb des gelöschten Bereichs → entfernen
        } else if (s.start < rangeStart && s.end > rangeEnd) {
            // Fall 4: Span umschließt den gelöschten Bereich → schrumpfen
            result.push({ ...s, end: s.end - deleteLen })
        } else if (s.start < rangeStart && s.end <= rangeEnd) {
            // Fall 5: Links-überlappend — Span-Ende wird beschnitten
            const newEnd = rangeStart
            if (newEnd > s.start) {
                result.push({ ...s, end: newEnd })
            }
        } else if (s.start >= rangeStart && s.end > rangeEnd) {
            // Fall 6: Rechts-überlappend — Span-Start wird beschnitten
            const newStart = rangeStart
            const newEnd = s.end - deleteLen
            if (newEnd > newStart) {
                result.push({ ...s, start: newStart, end: newEnd })
            }
        }
    }
    return result
}

/**
 * Verschiebt alle Span-Offsets um delta (für Paragraph-Merge).
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} delta
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function shiftSpans(spans, delta) {
    return spans.map(s => ({ ...s, start: s.start + delta, end: s.end + delta }))
}

/**
 * Teilt Spans an einem Offset in zwei Hälften.
 * Rechte Hälfte wird auf Offset 0 normalisiert.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} offset
 * @returns {{left: Array, right: Array}}
 */
export function splitSpansAtOffset(spans, offset) {
    const left = []
    const right = []
    for (const s of spans) {
        if (s.end <= offset) {
            // Komplett links
            left.push({ ...s })
        } else if (s.start >= offset) {
            // Komplett rechts → normalisieren
            right.push({ ...s, start: s.start - offset, end: s.end - offset })
        } else {
            // Span wird gesplittet
            left.push({ ...s, end: offset })
            const newStart = 0
            const newEnd = s.end - offset
            if (newEnd > newStart) {
                right.push({ ...s, start: newStart, end: newEnd })
            }
        }
    }
    return { left, right }
}

// ===================================================================
// Kategorie B: Link-Adjustment
// ===================================================================

/**
 * Passt Links nach einer Einfügung an.
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} insertOffset
 * @param {number} length
 * @returns {Array<{offset: number, targetId: number}>}
 */
export function adjustLinksForInsert(links, insertOffset, length) {
    if (length <= 0) return links.map(l => ({ ...l }))
    return links.map(l => {
        if (l.offset >= insertOffset) {
            return { ...l, offset: l.offset + length }
        }
        return { ...l }
    })
}

/**
 * Passt Links nach dem Löschen eines einzelnen Zeichens an.
 * Link bei deleteOffset wird entfernt (UFFFC gelöscht).
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} deleteOffset
 * @returns {Array<{offset: number, targetId: number}>}
 */
export function adjustLinksForDelete(links, deleteOffset) {
    const result = []
    for (const l of links) {
        if (l.offset === deleteOffset) {
            // Link wird gelöscht
        } else if (l.offset > deleteOffset) {
            result.push({ ...l, offset: l.offset - 1 })
        } else {
            result.push({ ...l })
        }
    }
    return result
}

/**
 * Passt Links nach dem Löschen eines Bereichs an.
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} rangeStart
 * @param {number} rangeEnd
 * @returns {Array<{offset: number, targetId: number}>}
 */
export function adjustLinksForDeleteRange(links, rangeStart, rangeEnd) {
    if (rangeStart >= rangeEnd) return links.map(l => ({ ...l }))
    const deleteLen = rangeEnd - rangeStart
    const result = []
    for (const l of links) {
        if (l.offset < rangeStart) {
            result.push({ ...l })
        } else if (l.offset >= rangeEnd) {
            result.push({ ...l, offset: l.offset - deleteLen })
        }
        // Links innerhalb des Bereichs werden entfernt
    }
    return result
}

/**
 * Verschiebt alle Link-Offsets um delta.
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} delta
 * @returns {Array<{offset: number, targetId: number}>}
 */
export function shiftLinks(links, delta) {
    return links.map(l => ({ ...l, offset: l.offset + delta }))
}

/**
 * Teilt Links an einem Offset in zwei Hälften.
 * Rechte Hälfte wird auf Offset 0 normalisiert.
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} offset
 * @returns {{left: Array, right: Array}}
 */
export function splitLinksAtOffset(links, offset) {
    const left = []
    const right = []
    for (const l of links) {
        if (l.offset < offset) {
            left.push({ ...l })
        } else {
            right.push({ ...l, offset: l.offset - offset })
        }
    }
    return { left, right }
}

// ===================================================================
// Kategorie C: Format-Operationen
// ===================================================================

/**
 * Merged benachbarte/überlappende Spans gleichen Typs.
 * Eingabe muss nach start sortiert sein.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function mergeAdjacentSpans(spans) {
    if (spans.length === 0) return []
    // Nach type gruppieren, innerhalb jeder Gruppe nach start sortieren
    const byType = new Map()
    for (const s of spans) {
        if (!byType.has(s.type)) byType.set(s.type, [])
        byType.get(s.type).push({ ...s })
    }
    const result = []
    for (const [type, group] of byType) {
        group.sort((a, b) => a.start - b.start)
        let current = group[0]
        for (let i = 1; i < group.length; i++) {
            const next = group[i]
            if (next.start <= current.end) {
                // Überlappend oder angrenzend → mergen
                current = { type, start: current.start, end: Math.max(current.end, next.end) }
            } else {
                result.push(current)
                current = next
            }
        }
        result.push(current)
    }
    // Ergebnis nach start sortieren
    result.sort((a, b) => a.start - b.start)
    return result
}

/**
 * Erweitert bestehenden Span oder erstellt neuen. Merged adjacent.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} start
 * @param {number} end
 * @param {string} type
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function extendOrCreateSpan(spans, start, end, type) {
    if (start >= end) return spans.map(s => ({ ...s }))
    const newSpans = spans.map(s => ({ ...s }))
    newSpans.push({ start, end, type })
    return mergeAdjacentSpans(newSpans)
}

/**
 * Entfernt Format aus einem Bereich. Beschneidet/splittet Spans.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} start
 * @param {number} end
 * @param {string} type
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function removeFormatFromRange(spans, start, end, type) {
    if (start >= end) return spans.map(s => ({ ...s }))
    const result = []
    for (const s of spans) {
        if (s.type !== type) {
            result.push({ ...s })
            continue
        }
        // Kein Überlapp
        if (s.end <= start || s.start >= end) {
            result.push({ ...s })
            continue
        }
        // Links-Rest (Span-Anfang vor Bereich)
        if (s.start < start) {
            result.push({ ...s, end: start })
        }
        // Rechts-Rest (Span-Ende nach Bereich)
        if (s.end > end) {
            result.push({ ...s, start: end })
        }
        // Span komplett innerhalb des Bereichs → entfernen
    }
    result.sort((a, b) => a.start - b.start)
    return result
}

/**
 * Prüft ob ein Bereich komplett von einem Format-Typ abgedeckt ist.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} start
 * @param {number} end
 * @param {string} type
 * @returns {boolean}
 */
export function isRangeCovered(spans, start, end, type) {
    if (start >= end) return true
    // Relevante Spans filtern und nach start sortieren
    const relevant = spans
        .filter(s => s.type === type && s.start < end && s.end > start)
        .sort((a, b) => a.start - b.start)
    if (relevant.length === 0) return false
    // Lückenlose Abdeckung prüfen
    let covered = start
    for (const s of relevant) {
        if (s.start > covered) return false
        covered = Math.max(covered, s.end)
        if (covered >= end) return true
    }
    return covered >= end
}

/**
 * Aktive Format-Typen an einer Position (Zeichen AT offset).
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} offset
 * @returns {Set<string>}
 */
export function getFormatsAtOffset(spans, offset) {
    const result = new Set()
    for (const s of spans) {
        if (s.start <= offset && offset < s.end) {
            result.add(s.type)
        }
    }
    return result
}

/**
 * Formate des Zeichens LINKS vom Cursor (für _activeFormats).
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} offset - Cursor-Position
 * @returns {Set<string>}
 */
export function getFormatsBeforeOffset(spans, offset) {
    if (offset <= 0) return new Set()
    return getFormatsAtOffset(spans, offset - 1)
}

/**
 * Extrahiert Spans in einem Bereich und normalisiert auf Start 0.
 * @param {Array<{start: number, end: number, type: string}>} spans
 * @param {number} start
 * @param {number} end
 * @returns {Array<{start: number, end: number, type: string}>}
 */
export function getSpansInRange(spans, start, end) {
    if (start >= end) return []
    const result = []
    for (const s of spans) {
        if (s.end <= start || s.start >= end) continue
        const clampedStart = Math.max(s.start, start)
        const clampedEnd = Math.min(s.end, end)
        if (clampedStart < clampedEnd) {
            result.push({ type: s.type, start: clampedStart - start, end: clampedEnd - start })
        }
    }
    result.sort((a, b) => a.start - b.start)
    return result
}

/**
 * Extrahiert Links in einem Bereich und normalisiert auf Start 0.
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {number} start
 * @param {number} end
 * @returns {Array<{offset: number, targetId: number}>}
 */
export function getLinksInRange(links, start, end) {
    if (start >= end) return []
    const result = []
    for (const l of links) {
        if (l.offset >= start && l.offset < end) {
            result.push({ ...l, offset: l.offset - start })
        }
    }
    return result
}

// ===================================================================
// Kategorie D: Konvertierung
// ===================================================================

/**
 * Konvertiert Markdown-Text zu Rich-Text-Datenmodell.
 * 1. Block-Format erkennen (# → heading, - → bullet, > → blockquote)
 * 2. findFormatSpans() für Inline-Formate
 * 3. findLinkSpans() für [[@id]]-Links
 * 4. Marker entfernen, Offset-Mapping aufbauen
 * 5. Spans auf Marker-freie Offsets umrechnen
 * 6. [[@id]] → \uFFFC + LinkEntry
 *
 * @param {string} markdownText - Markdown-Text (ein Absatz, ohne Zeilenumbruch)
 * @returns {{text: string, formats: Array, links: Array,
 *            headingLevel: number, listType: string|null, listLevel: number, blockquote: boolean}}
 */
export function markdownToRichText(markdownText) {
    if (!markdownText) {
        return { text: '', formats: [], links: [], headingLevel: 0, listType: null, listLevel: 0, blockquote: false, codeBlock: false }
    }

    // Schritt 1: Block-Format erkennen und Prefix entfernen
    let remaining = markdownText
    let headingLevel = 0
    let listType = null
    let listLevel = 0
    let blockquote = false

    // Blockquote: > am Anfang
    if (remaining.startsWith('> ')) {
        blockquote = true
        remaining = remaining.substring(2)
    }

    // Heading: # bis ######
    const headingMatch = remaining.match(/^(#{1,6})\s/)
    if (headingMatch) {
        headingLevel = headingMatch[1].length
        remaining = remaining.substring(headingMatch[0].length)
    }

    // Liste: - oder Ziffern. (mit Einrückung für Stufen)
    if (!headingLevel) {
        const bulletMatch = remaining.match(/^(\s*)[-•]\s/)
        if (bulletMatch) {
            listType = 'bullet'
            listLevel = Math.floor(bulletMatch[1].length / 2)
            remaining = remaining.substring(bulletMatch[0].length)
        } else {
            const numberedMatch = remaining.match(/^(\s*)\d+\.\s/)
            if (numberedMatch) {
                listType = 'numbered'
                listLevel = Math.floor(numberedMatch[1].length / 2)
                remaining = remaining.substring(numberedMatch[0].length)
            }
        }
    }

    // Schritt 2: Links finden und durch \uFFFC ersetzen
    const linkSpansInMarkdown = findLinkSpans(remaining)
    const links = []
    // Von hinten nach vorne ersetzen, damit Offsets stabil bleiben
    let withLinks = remaining
    for (let i = linkSpansInMarkdown.length - 1; i >= 0; i--) {
        const ls = linkSpansInMarkdown[i]
        withLinks = withLinks.substring(0, ls.start) + '\uFFFC' + withLinks.substring(ls.end)
    }
    // Link-Offsets im Text mit Links (vor Marker-Entfernung!)
    // Wir müssen erst die Format-Marker entfernen, dann die endgültigen Offsets berechnen

    // Schritt 3: Inline-Formate finden (auf Text mit \uFFFC statt Link-Marker)
    const formatSpans = findFormatSpans(withLinks)

    // Schritt 4: Offset-Mapping aufbauen (Markdown-Offsets → Clean-Offsets)
    // Marker-Positionen sammeln
    const markerPositions = new Set()
    for (const span of formatSpans) {
        for (let i = span.start; i < span.start + span.markerLen; i++) markerPositions.add(i)
        for (let i = span.end - span.markerLen; i < span.end; i++) markerPositions.add(i)
    }

    // Clean-Text aufbauen und Mapping erstellen
    // mdToClean[mdOffset] = cleanOffset (für Nicht-Marker-Positionen)
    const mdToClean = new Array(withLinks.length + 1)
    let cleanOffset = 0
    let cleanText = ''
    for (let i = 0; i < withLinks.length; i++) {
        mdToClean[i] = cleanOffset
        if (!markerPositions.has(i)) {
            cleanText += withLinks[i]
            cleanOffset++
        }
    }
    mdToClean[withLinks.length] = cleanOffset

    // Schritt 5: Format-Spans auf clean Offsets umrechnen
    const formats = []
    for (const span of formatSpans) {
        const contentStart = span.start + span.markerLen
        const contentEnd = span.end - span.markerLen
        const newStart = mdToClean[contentStart]
        const newEnd = mdToClean[contentEnd]
        if (newEnd > newStart) {
            formats.push({ start: newStart, end: newEnd, type: span.type })
        }
    }

    // Schritt 6: Link-Offsets auf clean Offsets umrechnen
    // Links wurden bereits zu \uFFFC umgewandelt — ihre Position im withLinks-String finden
    for (let i = 0; i < withLinks.length; i++) {
        if (withLinks[i] === '\uFFFC') {
            const cleanPos = mdToClean[i]
            // Zugehörige targetId finden — Links sind in der Reihenfolge wie im Text
            const linkIdx = links.length
            // Wir müssen die Link-Spans in Reihenfolge zuordnen
            links.push({ offset: cleanPos, targetId: 0 }) // Platzhalter
        }
    }
    // targetIds zuweisen (linkSpansInMarkdown ist nach Position sortiert)
    // Aber wir haben von hinten nach vorne ersetzt, die Reihenfolge im Text ist trotzdem korrekt
    // da \uFFFC in der gleichen Reihenfolge im String steht
    let linkIdx = 0
    for (let i = 0; i < linkSpansInMarkdown.length; i++) {
        if (linkIdx < links.length) {
            links[linkIdx].targetId = linkSpansInMarkdown[i].targetId
            linkIdx++
        }
    }

    return {
        text: cleanText,
        formats: mergeAdjacentSpans(formats),
        links,
        headingLevel,
        listType,
        listLevel,
        blockquote,
        codeBlock: false
    }
}

/**
 * Multi-Line-Markdown → RT pro Zeile: zerlegt eine Zeilenliste in
 * Paragraph-Daten und erkennt dabei Fenced-Code-Blocks (` ``` ` am
 * Zeilenanfang, optional mit Sprach-Hint, der verworfen wird). Zeilen
 * innerhalb eines Fences werden zu codeBlock-Paragraphs ohne
 * Inline-Format-Parsing — Fence-Marker selbst landen NICHT im Output.
 *
 * Edge-Cases:
 *   - Unbalancierter Fence (kein Close bis EOF): die offenen Code-Zeilen
 *     bleiben als codeBlock-Paragraphs erhalten.
 *   - Leerer Fence (` ``` `\n` ``` ` direkt hintereinander): kein Output-Para.
 *   - Inline-Backticks ohne führende Triple-Backticks am Zeilenanfang sind
 *     keine Fence (z.B. `` `code` `` als Inline-Format).
 *
 * @param {string[]} lines
 * @returns {Array<{text:string, formats:Array, links:Array,
 *                  headingLevel:number, listType:string|null, listLevel:number,
 *                  blockquote:boolean, codeBlock:boolean}>}
 */
export function markdownLinesToRichText(lines) {
    const out = []
    let inFence = false
    for (const line of (lines || [])) {
        // Fence-Erkennung: 3+ Backticks am Zeilenanfang, beliebiger Rest (Sprach-Hint).
        if (/^```/.test(line)) {
            inFence = !inFence
            continue
        }
        if (inFence) {
            out.push({
                text: line,
                formats: [],
                links: [],
                headingLevel: 0,
                listType: null,
                listLevel: 0,
                blockquote: false,
                codeBlock: true
            })
        } else {
            out.push(markdownToRichText(line))
        }
    }
    return out
}

/**
 * Konvertiert Rich-Text zurück zu Markdown (nur Inline-Formate).
 * @param {string} text - Reiner Text (mit \uFFFC für Links)
 * @param {Array<{start: number, end: number, type: string}>} formats
 * @param {Array<{offset: number, targetId: number}>} [links]
 * @returns {string}
 */
export function richTextToMarkdown(text, formats, links, linkLabels = null) {
    if (!text && (!formats || formats.length === 0)) return ''

    // Repräsentation eines Links: mit linkLabels → Klartext-Titel (Export),
    // sonst der rohe [[@id]]-Marker (roundtrip-treu für Persistenz/Import).
    const linkRepr = (targetId) => (linkLabels && linkLabels.has(targetId))
        ? linkLabels.get(targetId)
        : `[[@${targetId}]]`

    // Schritt 1: Links auflösen — \uFFFC → [[@id]] (von hinten, Offsets bleiben stabil)
    let result = text || ''
    const sortedLinksDesc = links ? [...links].sort((a, b) => b.offset - a.offset) : []
    for (const link of sortedLinksDesc) {
        if (link.offset >= 0 && link.offset < result.length && result[link.offset] === '\uFFFC') {
            result = result.substring(0, link.offset) + linkRepr(link.targetId) + result.substring(link.offset + 1)
        }
    }

    // Schritt 2: Offset-Mapping aufbauen (clean → result mit aufgelösten Links)
    const sortedLinksAsc = links ? [...links].sort((a, b) => a.offset - b.offset) : []
    let cumulativeAdd = 0
    const cleanToResult = new Array((text || '').length + 1)
    let linkIx = 0
    for (let i = 0; i <= (text || '').length; i++) {
        cleanToResult[i] = i + cumulativeAdd
        if (linkIx < sortedLinksAsc.length && sortedLinksAsc[linkIx].offset === i) {
            // An dieser Position wurde \uFFFC (1 Zeichen) durch [[@id]] ersetzt
            const idStr = linkRepr(sortedLinksAsc[linkIx].targetId)
            cumulativeAdd += idStr.length - 1
            linkIx++
        }
    }

    // Schritt 3: Format-Marker einfügen (von hinten, Offsets stabil)
    // Marker-Typ → Marker-String
    const markerMap = {
        bold: '**',
        italic: '*',
        underline: '_',
        strikethrough: '~~',
        code: '`'
    }

    // Einfügepunkte sammeln (Position im result-String + Marker-String)
    const insertPoints = []
    for (const span of formats) {
        const marker = markerMap[span.type]
        if (!marker) continue
        const resultStart = cleanToResult[span.start]
        const resultEnd = cleanToResult[span.end]
        insertPoints.push({ pos: resultEnd, marker, order: 1 })    // Schließend zuerst
        insertPoints.push({ pos: resultStart, marker, order: 0 })  // Öffnend
    }

    // Von hinten nach vorne sortieren (gleiche Position: schließend vor öffnend)
    insertPoints.sort((a, b) => b.pos - a.pos || b.order - a.order)

    for (const ip of insertPoints) {
        result = result.substring(0, ip.pos) + ip.marker + result.substring(ip.pos)
    }

    return result
}

/**
 * Konvertiert Rich-Text zurück zu vollständigem Markdown (Block + Inline).
 *
 * Code-Blöcke werden hier per-Paragraph als ` ``` `+text+` ``` ` gewickelt
 * und die Inline-Formate weggelassen (Code ist raw). Mehrzeilige
 * Codeblöcke entstehen erst, wenn Aufrufer aufeinanderfolgende code-Paragraphs
 * zu einem gemeinsamen Block zusammenfassen (siehe Multi-Line-Helper).
 *
 * @param {string} text
 * @param {Array} formats
 * @param {number} headingLevel
 * @param {string|null} listType
 * @param {number} listLevel
 * @param {boolean} blockquote
 * @param {Array} [links]
 * @param {boolean} [codeBlock=false]
 * @returns {string}
 */
export function richTextToMarkdownFull(text, formats, headingLevel, listType, listLevel, blockquote, links, codeBlock = false, linkLabels = null) {
    if (codeBlock) {
        // Raw-Inhalt — keine Inline-Format-Marker, keine Block-Prefixe.
        return '```\n' + (text || '') + '\n```'
    }

    const inline = richTextToMarkdown(text, formats, links, linkLabels)

    let prefix = ''

    // Blockquote-Prefix
    if (blockquote) {
        prefix += '> '
    }

    // Heading-Prefix
    if (headingLevel > 0) {
        prefix += '#'.repeat(headingLevel) + ' '
    }

    // Listen-Prefix
    if (listType === 'bullet') {
        prefix += '  '.repeat(listLevel) + '- '
    } else if (listType === 'numbered') {
        prefix += '  '.repeat(listLevel) + '1. '
    }

    return prefix + inline
}

// ===================================================================
// Kategorie E: Serialisierung (Persistenz)
// ===================================================================

/**
 * Serialisiert einen Paragraph für RT-Persistenz (localStorage v5, CBL v4.0).
 * Kompakt: nur nicht-default Felder werden geschrieben.
 *
 * opts.includeProjectState: true → projekt-lokale State-Felder (sectionCollapsed,
 *   activeCollapsed) werden mitserialisiert. Default false für CBL-Export (diese
 *   Felder sind UI-State, kein Dokumenten-Inhalt); true für IndexedDB/Cloud-Sync.
 *
 * @param {Object} para - Paragraph-Objekt
 * @param {Object} [opts]
 * @param {boolean} [opts.includeProjectState=false]
 * @returns {Object} Kompaktes JSON-Objekt
 */
export function serializeParagraphRT(para, opts = {}) {
    const includeProjectState = opts.includeProjectState === true

    // Separatoren: nur Struktur-Felder
    if (para.type === 'separator') {
        const entry = { type: 'separator', text: para.text || '', outlineDepth: para.outlineDepth }
        if (para.id != null) entry.id = para.id
        if (para.locked) entry.locked = true
        if (includeProjectState && para.sectionCollapsed) entry.sectionCollapsed = true
        return entry
    }

    // Outline-Item: nur Strukturfelder + Note-Formats/Links.
    // Spec §7.1: { id, type, note, outlineDepth (optional activeCollapsed) }.
    // Text, info, Block-Metadaten und text/info-Formats/Links sind an
    // Outline-Items per Definition leer und werden weggelassen.
    if (para.type === 'outline-item') {
        const entry = { type: 'outline-item', note: para.note || '', outlineDepth: para.outlineDepth || 0 }
        if (para.id != null) entry.id = para.id
        if (includeProjectState && para.activeCollapsed) entry.activeCollapsed = true
        if (para.formats && para.formats.note && para.formats.note.length > 0) {
            entry.formats = { note: para.formats.note }
        }
        if (para.links && para.links.note && para.links.note.length > 0) {
            entry.links = { note: para.links.note }
        }
        return entry
    }

    // Normal-Para: nur nicht-Default-Felder serialisieren.
    // Outline-Struktur lebt ausschliesslich an Outline-Item-Paras (siehe oben).
    // Tiefe an Normal-Paras bleibt nur fuer Background-Separatoren
    // (outlineDepth = -1) relevant.
    const entry = { text: para.text }
    if (para.outlineDepth) entry.outlineDepth = para.outlineDepth
    if (includeProjectState && para.activeCollapsed) entry.activeCollapsed = true

    // Nur wenn nicht default
    if (para.type !== 'normal') entry.type = para.type
    if (para.note) entry.note = para.note
    if (para.info) entry.info = para.info
    if (para.id != null) entry.id = para.id

    // Formats: nur nicht-leere Felder
    const formats = {}
    if (para.formats) {
        if (para.formats.text && para.formats.text.length > 0) formats.text = para.formats.text
        if (para.formats.note && para.formats.note.length > 0) formats.note = para.formats.note
        if (para.formats.info && para.formats.info.length > 0) formats.info = para.formats.info
    }
    if (Object.keys(formats).length > 0) entry.formats = formats

    // Links: nur nicht-leere Felder
    const links = {}
    if (para.links) {
        if (para.links.text && para.links.text.length > 0) links.text = para.links.text
        if (para.links.note && para.links.note.length > 0) links.note = para.links.note
        if (para.links.info && para.links.info.length > 0) links.info = para.links.info
    }
    if (Object.keys(links).length > 0) entry.links = links

    // Block-Metadaten: nur wenn truthy/> 0
    if (para.headingLevel > 0) entry.headingLevel = para.headingLevel
    if (para.listType) entry.listType = para.listType
    if (para.listLevel > 0) entry.listLevel = para.listLevel
    if (para.listStart > 1) entry.listStart = para.listStart
    if (para.blockquote) entry.blockquote = para.blockquote
    if (para.codeBlock) entry.codeBlock = para.codeBlock

    return entry
}

/**
 * Deserialisiert RT-Daten zu einem vollständigen Feld-Objekt.
 * Rekonstruiert alle Felder mit Defaults.
 * @param {Object} data - Serialisierte Daten
 * @returns {Object} Vollständiges Feld-Objekt (direkt auf Paragraph setzbar)
 */
export function deserializeParagraphRT(data) {
    return {
        text: data.text || '',
        type: data.type || 'normal',
        note: data.note || '',
        info: data.info || '',
        outlineDepth: data.outlineDepth || 0,
        id: data.id != null ? data.id : null,
        formats: {
            text: (data.formats && data.formats.text) ? data.formats.text.map(f => ({ ...f })) : [],
            note: (data.formats && data.formats.note) ? data.formats.note.map(f => ({ ...f })) : [],
            info: (data.formats && data.formats.info) ? data.formats.info.map(f => ({ ...f })) : []
        },
        links: {
            text: (data.links && data.links.text) ? data.links.text.map(l => ({ ...l })) : [],
            note: (data.links && data.links.note) ? data.links.note.map(l => ({ ...l })) : [],
            info: (data.links && data.links.info) ? data.links.info.map(l => ({ ...l })) : []
        },
        headingLevel: data.headingLevel || 0,
        listType: data.listType || null,
        listLevel: data.listLevel || 0,
        listStart: data.listStart || 1,
        blockquote: data.blockquote || false,
        codeBlock: data.codeBlock || false,
        locked: data.locked || false,
        sectionCollapsed: data.sectionCollapsed || false,
        activeCollapsed: data.activeCollapsed || false
    }
}

/**
 * Konvertiert Rich-Text zu HTML.
 * Erzeugt <b>, <i>, <u>, <s> Tags für Format-Spans.
 * Multi-Paragraph: Zeilen werden mit <br> getrennt.
 * @param {string} text - Reiner Text
 * @param {Array<{start: number, end: number, type: string}>} formats
 * @returns {string} HTML-String
 */
export function richTextToHtml(text, formats) {
    if (!text) return ''

    // Kein Format → nur HTML-escapen
    if (!formats || formats.length === 0) {
        return _escapeHtml(text)
    }

    // Tag-Mapping
    const FORMAT_TO_TAG = {
        bold: 'b', italic: 'i', underline: 'u', strikethrough: 's', code: 'code'
    }

    // Einfügepunkte sammeln: {offset, action: 'open'|'close', type, order}
    const points = []
    for (const span of formats) {
        const tag = FORMAT_TO_TAG[span.type]
        if (!tag) continue
        points.push({ offset: span.start, action: 'open', tag, type: span.type })
        points.push({ offset: span.end, action: 'close', tag, type: span.type })
    }

    // Sortieren: nach Offset, gleicher Offset → close vor open,
    // bei gleicher Aktion: open in Originalreihenfolge, close in umgekehrter
    points.sort((a, b) => {
        if (a.offset !== b.offset) return a.offset - b.offset
        if (a.action !== b.action) return a.action === 'close' ? -1 : 1
        return 0
    })

    // HTML aufbauen
    let result = ''
    let lastOffset = 0
    for (const p of points) {
        // Text zwischen letztem Punkt und aktuellem Offset escapen
        if (p.offset > lastOffset) {
            result += _escapeHtml(text.substring(lastOffset, p.offset))
        }
        lastOffset = p.offset
        if (p.action === 'open') {
            result += `<${p.tag}>`
        } else {
            result += `</${p.tag}>`
        }
    }
    // Rest-Text
    if (lastOffset < text.length) {
        result += _escapeHtml(text.substring(lastOffset))
    }

    return result
}

/** HTML-Escape für Text-Content */
function _escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Konvertiert HTML-Text zu Rich-Text-Datenmodell.
 * Erkennt <b>, <strong>, <i>, <em>, <u>, <s>, <del>, <strike> Tags.
 * Entfernt alle anderen HTML-Tags. Konvertiert <br>, <p>, <div> zu Zeilenumbrüchen.
 * @param {string} html - HTML-String
 * @returns {Array<{text: string, formats: Array, links: Array}>} Ein Entry pro Zeile
 */
export function htmlToRichText(html) {
    if (!html) return [{ text: '', formats: [], links: [] }]

    // DOM-basiertes Parsing für korrekte Block-Struktur
    const doc = new DOMParser().parseFromString(html, 'text/html')

    // Format-Tag-Mapping (Inline)
    const TAG_TO_FORMAT = {
        'b': 'bold', 'strong': 'bold',
        'i': 'italic', 'em': 'italic',
        'u': 'underline',
        's': 'strikethrough', 'del': 'strikethrough', 'strike': 'strikethrough',
        'code': 'code', 'tt': 'code', 'kbd': 'code', 'samp': 'code'
    }

    // Ergebnis: Array von Absätzen mit Text, Formats und Block-Attributen
    const result = []
    let currentText = ''
    const currentFormats = []  // [{start, end, type}]
    const formatStack = []     // [{type, startOffset}]

    // Block-Kontext: welcher Block-Typ ist aktiv?
    let blockContext = { listType: null, listLevel: -1, blockquote: false, headingLevel: 0 }

    // Absatz abschließen und zum Ergebnis hinzufügen
    function flushParagraph() {
        if (currentText.length === 0) return
        // Inline-Zeilenumbrüche als separate Absätze behandeln
        const lines = currentText.split('\n')
        for (let li = 0; li < lines.length; li++) {
            const lineText = lines[li]
            // Offset im currentText berechnen
            let lineStart = 0
            for (let j = 0; j < li; j++) lineStart += lines[j].length + 1

            const lineSpans = []
            for (const span of currentFormats) {
                if (span.end <= lineStart || span.start >= lineStart + lineText.length) continue
                const s = Math.max(span.start, lineStart) - lineStart
                const e = Math.min(span.end, lineStart + lineText.length) - lineStart
                if (e > s) lineSpans.push({ start: s, end: e, type: span.type })
            }

            const entry = {
                text: lineText,
                formats: mergeAdjacentSpans(lineSpans),
                links: []
            }
            // Block-Attribute nur auf die erste Sub-Zeile setzen (bei <br> innerhalb eines Blocks)
            if (li === 0) {
                if (blockContext.listType) {
                    entry.listType = blockContext.listType
                    entry.listLevel = blockContext.listLevel
                }
                if (blockContext.blockquote) entry.blockquote = true
                if (blockContext.headingLevel > 0) entry.headingLevel = blockContext.headingLevel
            }
            result.push(entry)
        }
        currentText = ''
        currentFormats.length = 0
    }

    // Listen-Tiefe zählen (verschachtelte ul/ol)
    let listDepth = -1

    // Rekursiver DOM-Walker
    function walk(node) {
        if (node.nodeType === 3) {
            // Text-Knoten: Text anfügen (\u00A0 = &nbsp; → normales Leerzeichen)
            const text = node.textContent?.replace(/\u00A0/g, ' ')
            if (text) currentText += text
            return
        }

        if (node.nodeType !== 1) return // Nur Element-Knoten

        const tag = node.tagName.toLowerCase()

        // Block-Level-Elemente
        if (tag === 'ul' || tag === 'ol') {
            listDepth++
            for (const child of node.childNodes) walk(child)
            listDepth--
            return
        }

        if (tag === 'li') {
            flushParagraph()
            const parentTag = node.parentElement?.tagName?.toLowerCase()
            blockContext = {
                listType: parentTag === 'ol' ? 'numbered' : 'bullet',
                listLevel: Math.max(0, listDepth),
                blockquote: false,
                headingLevel: 0
            }
            for (const child of node.childNodes) walk(child)
            flushParagraph()
            blockContext = { listType: null, listLevel: -1, blockquote: false, headingLevel: 0 }
            return
        }

        if (tag === 'blockquote') {
            flushParagraph()
            const prevContext = { ...blockContext }
            blockContext = { ...blockContext, blockquote: true }
            for (const child of node.childNodes) walk(child)
            flushParagraph()
            blockContext = prevContext
            return
        }

        const headingMatch = tag.match(/^h([1-6])$/)
        if (headingMatch) {
            flushParagraph()
            blockContext = { listType: null, listLevel: -1, blockquote: false, headingLevel: parseInt(headingMatch[1]) }
            for (const child of node.childNodes) walk(child)
            flushParagraph()
            blockContext = { listType: null, listLevel: -1, blockquote: false, headingLevel: 0 }
            return
        }

        if (tag === 'p' || tag === 'div') {
            flushParagraph()
            for (const child of node.childNodes) walk(child)
            flushParagraph()
            return
        }

        if (tag === 'br') {
            currentText += '\n'
            return
        }

        // Inline-Format-Tags
        const formatType = TAG_TO_FORMAT[tag]
        if (formatType) {
            const startOffset = currentText.length
            formatStack.push({ type: formatType, startOffset })
            for (const child of node.childNodes) walk(child)
            formatStack.pop()
            if (currentText.length > startOffset) {
                currentFormats.push({ start: startOffset, end: currentText.length, type: formatType })
            }
            return
        }

        // Unbekannte Tags: Kinder verarbeiten
        for (const child of node.childNodes) walk(child)
    }

    walk(doc.body)
    flushParagraph()

    // Leere Trailing-Zeilen entfernen
    while (result.length > 1 && result[result.length - 1].text === '') {
        result.pop()
    }

    return result.length > 0 ? result : [{ text: '', formats: [], links: [] }]
}

// ===================================================================
// Kategorie F: Rendering (buildStyledChars)
// ===================================================================

// Style-Mapping: Format-Kombination → Style-String
// Reihenfolge: bold, italic entscheiden den Base-Style,
// underline/strikethrough werden als +UL/+ST Suffixe angehängt
const _BASE_STYLE_MAP = {
    'EDITOR_TEXT':       { bold: 'EDITOR_BOLD',   italic: 'EDITOR_ITALIC',   boldItalic: 'EDITOR_BOLD_ITALIC' },
    'CONSOLE_INPUT':     { bold: 'EDITOR_BOLD',   italic: 'EDITOR_ITALIC',   boldItalic: 'EDITOR_BOLD_ITALIC' },
    'EDITOR_HEADING_1':  { bold: 'EDITOR_HEADING_1', italic: 'EDITOR_HEADING_1', boldItalic: 'EDITOR_HEADING_1' },
    'EDITOR_HEADING_2':  { bold: 'EDITOR_HEADING_2', italic: 'EDITOR_HEADING_2', boldItalic: 'EDITOR_HEADING_2' },
    'EDITOR_HEADING_3':  { bold: 'EDITOR_HEADING_3', italic: 'EDITOR_HEADING_3', boldItalic: 'EDITOR_HEADING_3' },
    'EDITOR_HEADING_4':  { bold: 'EDITOR_HEADING_4', italic: 'EDITOR_HEADING_4', boldItalic: 'EDITOR_HEADING_4' },
    'EDITOR_HEADING_5':  { bold: 'EDITOR_HEADING_5', italic: 'EDITOR_HEADING_5', boldItalic: 'EDITOR_HEADING_5' },
    'EDITOR_HEADING_6':  { bold: 'EDITOR_HEADING_6', italic: 'EDITOR_HEADING_6', boldItalic: 'EDITOR_HEADING_6' },
    // Im Blockquote (kursiv) wird *italic* zu Nicht-Kursiv gespiegelt — sonst wäre der
    // Marker ein No-Op. boldItalic bleibt fett-aufrecht (= bold im Blockquote).
    'EDITOR_BLOCKQUOTE': { bold: 'EDITOR_BOLD',   italic: 'EDITOR_BLOCKQUOTE_UNITALIC', boldItalic: 'EDITOR_BOLD' },
    'CONSOLE_AI_BLOCKQUOTE': { bold: 'EDITOR_BOLD', italic: 'CONSOLE_AI_BLOCKQUOTE_UNITALIC', boldItalic: 'EDITOR_BOLD' },
}

/**
 * Bestimmt den kombinierten Style für eine Zeichenposition mit gegebenen Formaten.
 *
 * Code dominiert: Wenn `code === true`, wird Bold/Italic/Strike/Underline
 * verworfen und der Style direkt auf EDITOR_CODE bzw. CONSOLE_AI_CODE gesetzt
 * (analog zum Markdown-Pfad in TextProcessor.parseToStyledChars Z. 499-501).
 * Im Carbisdale-Dialekt schuetzt ein Code-Span seinen Inhalt vor anderen
 * Inline-Formaten — Konsistenz mit dem Roundtrip.
 *
 * @param {string} baseStyle - Basis-Style (z.B. 'EDITOR_TEXT')
 * @param {boolean} bold
 * @param {boolean} italic
 * @param {boolean} underline
 * @param {boolean} strikethrough
 * @param {boolean} [code=false] - Wenn true, ueberschreibt alle anderen Flags
 * @returns {string} Style-String (z.B. 'EDITOR_BOLD+ST' oder 'EDITOR_CODE')
 */
export function combineStyle(baseStyle, bold, italic, underline, strikethrough, code = false) {
    if (code) {
        return baseStyle.startsWith('CONSOLE_') ? 'CONSOLE_AI_CODE' : 'EDITOR_CODE'
    }

    let style = baseStyle

    // Bold/Italic Kombination
    const map = _BASE_STYLE_MAP[baseStyle]
    if (map) {
        if (bold && italic) {
            style = map.boldItalic
        } else if (bold) {
            style = map.bold
        } else if (italic) {
            style = map.italic
        }
    } else {
        // Unbekannter Base-Style: Bold/Italic als generische Styles
        if (bold && italic) {
            style = 'EDITOR_BOLD_ITALIC'
        } else if (bold) {
            style = 'EDITOR_BOLD'
        } else if (italic) {
            style = 'EDITOR_ITALIC'
        }
    }

    // Suffix-Logik: +ST, +UL (Reihenfolge wie im bestehenden System)
    if (strikethrough) style += '+ST'
    if (underline) style += '+UL'

    return style
}

/**
 * Löst Link-Labels aus der SeparatorMap auf.
 * Zentralisiert die Label-Ermittlung fuer buildStyledChars und buildRichTextMapping.
 * @param {Array<{offset: number, targetId: number}>} links
 * @returns {Map<number, {targetId: number, label: string, isBroken: boolean}>}
 */
function _resolveLinkLabels(links, separatorMap) {
    const linkMap = new Map()
    if (!links || links.length === 0) return linkMap
    for (const link of links) {
        let label = `[[@${link.targetId}]]`
        let isBroken = true
        if (separatorMap) {
            const entry = separatorMap.get(link.targetId)
            if (entry) {
                label = entry.label || `§${link.targetId}`
                isBroken = entry.isBroken || false
            }
        }
        linkMap.set(link.offset, { targetId: link.targetId, label, isBroken })
    }
    return linkMap
}

/**
 * Baut ein Array von StyledChars aus Rich-Text-Daten.
 * Ersetzt parseToStyledChars/parseToStyledCharsWithLinks im Rich-Text-Pfad.
 *
 * @param {string} text - Reiner Text (mit \uFFFC für Links)
 * @param {Array<{start: number, end: number, type: string}>} formats - FormatSpan[]
 * @param {Array<{offset: number, targetId: number}>} links - LinkEntry[]
 * @param {string} baseStyle - Basis-Style (z.B. 'EDITOR_TEXT')
 * @param {Map<number, {label: string, isBroken: boolean}>|null} separatorMap - für Link-Label-Auflösung
 * @param {string|null} urlStyle - wenn gesetzt: Web-URLs (http/https) erkennen und
 *   mit diesem Style + isUrl/url-Flags markieren (Editor: 'EDITOR_LINK').
 *   null = keine URL-Erkennung (Console stylt URLs über eigenen Pfad).
 * @returns {Array<{char: string, style: string, dataOffset: number, isLink?: boolean, linkTargetId?: number, isUrl?: boolean, url?: string}>}
 */
export function buildStyledChars(text, formats, links, baseStyle, separatorMap, urlStyle = null) {
    if (!text) return []

    const linkMap = _resolveLinkLabels(links, separatorMap)
    const urlSpans = urlStyle ? findUrlSpans(text) : null
    let urlIdx = 0

    // Span-Grenzen vorberechnen fuer O(n+m) Traversierung statt O(n*m)
    // Events: +1 bei span.start (Format aktiv), -1 bei span.end (Format endet)
    const boldEvents = [], italicEvents = [], underlineEvents = [], strikeEvents = [], codeEvents = []
    for (const span of formats) {
        const target = span.type === 'bold' ? boldEvents
            : span.type === 'italic' ? italicEvents
            : span.type === 'underline' ? underlineEvents
            : span.type === 'strikethrough' ? strikeEvents
            : span.type === 'code' ? codeEvents : null
        if (target) {
            target.push({ pos: span.start, delta: 1 })
            target.push({ pos: span.end, delta: -1 })
        }
    }
    // Events nach Position sortieren
    const sortFn = (a, b) => a.pos - b.pos || a.delta - b.delta
    boldEvents.sort(sortFn)
    italicEvents.sort(sortFn)
    underlineEvents.sort(sortFn)
    strikeEvents.sort(sortFn)
    codeEvents.sort(sortFn)

    // Laufende Zaehler und Event-Indizes
    let boldCount = 0, italicCount = 0, underlineCount = 0, strikeCount = 0, codeCount = 0
    let bIdx = 0, iIdx = 0, uIdx = 0, sIdx = 0, cIdx = 0

    // Aktive Formate pro Zeichen scannen (Codepoint-aware fuer Emojis)
    const result = []
    for (let i = 0; i < text.length; ) {
        // Events an Position i verarbeiten
        while (bIdx < boldEvents.length && boldEvents[bIdx].pos <= i) boldCount += boldEvents[bIdx++].delta
        while (iIdx < italicEvents.length && italicEvents[iIdx].pos <= i) italicCount += italicEvents[iIdx++].delta
        while (uIdx < underlineEvents.length && underlineEvents[uIdx].pos <= i) underlineCount += underlineEvents[uIdx++].delta
        while (sIdx < strikeEvents.length && strikeEvents[sIdx].pos <= i) strikeCount += strikeEvents[sIdx++].delta
        while (cIdx < codeEvents.length && codeEvents[cIdx].pos <= i) codeCount += codeEvents[cIdx++].delta

        const cp = text.codePointAt(i)
        const ch = String.fromCodePoint(cp)

        // Link-Platzhalter: Labels emittieren (\uFFFC ist immer BMP, length 1)
        if (ch === '\uFFFC' && linkMap.has(i)) {
            const linkInfo = linkMap.get(i)
            const linkStyle = linkInfo.isBroken ? 'EDITOR_LINK_BROKEN' : 'EDITOR_LINK'
            const labelChars = linkInfo.label
            for (let j = 0; j < labelChars.length; j++) {
                result.push({
                    char: labelChars[j],
                    style: linkStyle,
                    dataOffset: i,
                    isLink: true,
                    linkTargetId: linkInfo.targetId
                })
            }
            i += 1
            continue
        }

        // Web-URL: Zeichen als klickbaren Link stylen (überstimmt Format-Spans,
        // analog zum atomaren Section-Link). Spans sind sortiert → laufender Index.
        if (urlSpans) {
            while (urlIdx < urlSpans.length && urlSpans[urlIdx].end <= i) urlIdx++
            if (urlIdx < urlSpans.length && i >= urlSpans[urlIdx].start && i < urlSpans[urlIdx].end) {
                result.push({
                    char: ch,
                    style: urlStyle,
                    dataOffset: i,
                    isUrl: true,
                    url: urlSpans[urlIdx].url
                })
                i += ch.length
                continue
            }
        }

        const style = combineStyle(baseStyle,
            boldCount > 0, italicCount > 0, underlineCount > 0, strikeCount > 0, codeCount > 0)
        result.push({ char: ch, style, dataOffset: i })
        i += ch.length  // Surrogate-Pair: 2, sonst 1
    }

    return result
}

/**
 * Baut ein Display-Mapping für Rich-Text mit Links (analog zu buildDisplayMappingWithLinks).
 * Nur nötig wenn Links vorhanden sind — ohne Links ist dataOffset === displayOffset.
 *
 * @param {string} text - Reiner Text (mit \uFFFC)
 * @param {Array<{offset: number, targetId: number}>} links
 * @param {Map<number, {label: string}>|null} separatorMap
 * @returns {{dataToDisplay: Int32Array, displayToData: Int32Array, displayLength: number}|null}
 */
export function buildRichTextMapping(text, links, separatorMap) {
    if (!links || links.length === 0) return null

    const linkLabels = _resolveLinkLabels(links, separatorMap)

    // Display-Länge und Display-Text berechnen
    let displayLength = 0
    const displayChars = []
    for (let i = 0; i < text.length; i++) {
        if (text[i] === '\uFFFC' && linkLabels.has(i)) {
            const { label } = linkLabels.get(i)
            displayLength += label.length
            // NBSP statt Leerzeichen damit Link beim Word-Wrap atomar bleibt
            for (let j = 0; j < label.length; j++) {
                displayChars.push(label[j] === ' ' ? '\u00A0' : label[j])
            }
        } else {
            displayLength++
            displayChars.push(text[i])
        }
    }
    const displayText = displayChars.join('')

    // Mappings aufbauen
    const dataToDisplay = new Int32Array(text.length + 1)
    const displayToData = new Int32Array(displayLength + 1)
    let dIdx = 0
    for (let i = 0; i < text.length; i++) {
        dataToDisplay[i] = dIdx
        if (text[i] === '\uFFFC' && linkLabels.has(i)) {
            const labelLen = linkLabels.get(i).label.length
            // Alle Display-Positionen des Labels zeigen auf den \uFFFC data-offset
            for (let j = 0; j < labelLen; j++) {
                displayToData[dIdx + j] = i
            }
            dIdx += labelLen
        } else {
            displayToData[dIdx] = i
            dIdx++
        }
    }
    dataToDisplay[text.length] = dIdx
    displayToData[displayLength] = text.length

    return { dataToDisplay, displayToData, displayLength, displayText }
}
